import React from 'react';
import {
  Control,
  FieldValues,
  useFieldArray,
  UseFieldArrayProps,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  AddOutlined,
  DeleteOutline,
  Euro,
  PercentOutlined,
} from '@mui/icons-material';
import { FormInput } from 'components';
import { DiscountTypes } from 'api';
import FreeGiftCollectionsInput from './FreeGiftCollectionsInput';

type Props<T extends FieldValues = any> = UseFieldArrayProps<T> & {
  setValue: UseFormSetValue<DiscountTypes.ItemCreateParams>;
  control: Control<DiscountTypes.ItemCreateParams> | undefined;
  watch: UseFormWatch<DiscountTypes.ItemCreateParams>;
};

const CartTierInputs: React.FC<Props> = (props: Props): JSX.Element => {
  const { name, keyName, shouldUnregister, rules, control, setValue, watch } =
    props;

  const appendValue: DiscountTypes.CartValueTier = {
    cart_value_threshold: 0,
    value_type: 'percent',
    percent_off: 0,
    free_gift_enabled: false,
    free_gift_quantity: 0,
    free_gift_collection_id: '',
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name,
    keyName,
    shouldUnregister,
    rules,
  });

  return (
    <Stack spacing={2}>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h5">Cart value tiers</Typography>
      </Box>
      {fields.map((field, index) => {
        const isFreeGiftEnabled = watch(
          `metafields_attributes.0.value.cart_value_tiers.${index}.free_gift_enabled`,
        );
        const valueType = watch(
          `metafields_attributes.0.value.cart_value_tiers.${index}.value_type`,
        );
        const freeGiftCollection = watch(
          `metafields_attributes.0.value.cart_value_tiers.${index}.free_gift_collection`,
        );
        const initialFreeGiftCollection = freeGiftCollection
          ? {
              _id: { $oid: freeGiftCollection.id },
              name: freeGiftCollection.title,
              image: freeGiftCollection.image,
            }
          : undefined;

        return (
          <Grid
            container
            key={field.id}
            columnGap={2}
            alignItems="center"
            wrap="nowrap"
          >
            <Grid item flexGrow={1}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Grid
                  container
                  key={field.id}
                  spacing={2}
                  alignItems="flex-end"
                >
                  <Grid item xs={12}>
                    <FormInput
                      control={control}
                      name={`${name}.${index}.cart_value_threshold`}
                      inputType="number"
                      label="Cart Value Threshold"
                      rules={{
                        required: true,
                        min: 0,
                        onBlur: (e) =>
                          setValue(
                            `metafields_attributes.${0}.value.cart_value_tiers.${index}.cart_value_threshold`,
                            +(+e.target.value).toFixed(2),
                          ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormInput
                      control={control}
                      name={`${name}.${index}.value_type`}
                      inputType="select"
                      label="Value type"
                      options={[
                        { label: 'Percent', value: 'percent' },
                        { label: 'Amount', value: 'amount' },
                      ]}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {valueType === 'amount' ? (
                      <FormInput
                        control={control}
                        name={`${name}.${index}.amount_off`}
                        inputType="number"
                        label="Amount off"
                        rules={{
                          required: true,
                          onBlur: (e) =>
                            setValue(
                              `metafields_attributes.${0}.value.cart_value_tiers.${index}.amount_off`,
                              +(+e.target.value).toFixed(2),
                            ),
                          min: 0,
                        }}
                        InputProps={{
                          endAdornment: <Euro />,
                          inputProps: {
                            min: 0,
                          },
                        }}
                      />
                    ) : (
                      <FormInput
                        control={control}
                        name={`${name}.${index}.percent_off`}
                        inputType="number"
                        label="Percent off"
                        rules={{
                          required: true,
                          onBlur: (e) =>
                            setValue(
                              `metafields_attributes.${0}.value.cart_value_tiers.${index}.percent_off`,
                              +(+e.target.value).toFixed(2),
                            ),
                          min: 0,
                          max: 100,
                        }}
                        InputProps={{
                          endAdornment: <PercentOutlined />,
                          inputProps: {
                            min: 0,
                            max: 100,
                          },
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Stack>
                      <FormInput
                        control={control}
                        name={`${name}.${index}.free_gift_enabled`}
                        inputType="checkbox"
                        label="Enable free gift"
                      />
                    </Stack>
                  </Grid>

                  {isFreeGiftEnabled && (
                    <>
                      <Grid item xs={12}>
                        <FormInput
                          control={control}
                          name={`${name}.${index}.free_gift_quantity`}
                          inputType="number"
                          label="Number of free gifts"
                          rules={{
                            min: 0,
                            onBlur: (e) =>
                              setValue(
                                `metafields_attributes.${0}.value.cart_value_tiers.${index}.free_gift_quantity`,
                                +(+e.target.value).toFixed(2),
                              ),
                          }}
                          InputProps={{
                            inputProps: {
                              step: 1,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FreeGiftCollectionsInput
                          name={`${name}.${index}.free_gift_collection_id`}
                          control={control}
                          watch={watch}
                          initialValue={initialFreeGiftCollection}
                          onChange={(value) => {
                            setValue(
                              `metafields_attributes.${0}.value.cart_value_tiers.${index}.free_gift_collection`,
                              value,
                            );
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Card>
            </Grid>
            <Grid item>
              <IconButton
                onClick={() => remove(index)}
                sx={(t) => ({
                  background: t.palette.error.main,
                  color: t.palette.common.white,
                  '&:focus,&:hover': {
                    background: t.palette.error.dark,
                  },
                })}
              >
                <DeleteOutline />
              </IconButton>
            </Grid>
          </Grid>
        );
      })}
      <Button
        fullWidth
        onClick={() => append(appendValue)}
        endIcon={<AddOutlined />}
      >
        Add more
      </Button>
    </Stack>
  );
};

export default CartTierInputs;
